import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/network/http';
import { adminApi } from '../api';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/features/auth/store/authStore';
import type { EmploymentRoleFormData } from '../types';

const KEYS = {
  all: ['employment-roles'] as const,
  list: (params?: Record<string, unknown>) => [...KEYS.all, 'list', params] as const,
  active: () => [...KEYS.all, 'active'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  rates: (id: string) => [...KEYS.all, 'rates', id] as const,
  hospitalSubset: (id: string) => [...KEYS.all, 'hospital-subset', id] as const,
};

export function useEmploymentRoles(params?: { page?: number; limit?: number; search?: string; isActive?: string }) {
  return useQuery({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: async () => {
      const { data } = await adminApi.getEmploymentRoles(params);
      return data;
    },
  });
}

export function useActiveEmploymentRoles() {
  return useQuery({
    queryKey: KEYS.active(),
    queryFn: async () => {
      const { data } = await adminApi.getActiveEmploymentRoles();
      return data.employmentRoles;
    },
  });
}

export function useEmploymentRoleDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await adminApi.getEmploymentRoleById(id!);
      return data.employmentRole;
    },
    enabled: !!id,
  });
}

export function useEmploymentRoleRates(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.rates(id!),
    queryFn: async () => {
      const { data } = await adminApi.getEmploymentRoleRates(id!);
      return data.rates;
    },
    enabled: !!id,
  });
}

export function useCreateEmploymentRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EmploymentRoleFormData) => {
      const { data: result } = await adminApi.createEmploymentRole(data);
      return result.employmentRole;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateEmploymentRole() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EmploymentRoleFormData> }) => {
      const { data: result } = await adminApi.updateEmploymentRole(id, data);
      return result.employmentRole;
    },
    onSuccess: async () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
      // Re-fetch current user so auth store gets fresh employmentRole data
      try {
        const { data } = await authApi.getMe();
        setUser(data.user);
        queryClient.setQueryData(['auth', 'profile'], data.user);
      } catch {
        // Current user may not have this role — ignore
      }
    },
  });
}

export function useDeleteEmploymentRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteEmploymentRole(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useCreateRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { reportTypeId: string; ratePerUnit: number; fieldOverrides?: string } }) => {
      const { data: result } = await adminApi.createEmploymentRoleRate(id, data);
      return result.rate;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateRate() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation({
    mutationFn: async ({ roleId, rateId, data }: { roleId: string; rateId: string; data: { ratePerUnit?: number; fieldOverrides?: string | null } }) => {
      const { data: result } = await adminApi.updateEmploymentRoleRate(roleId, rateId, data);
      return result.rate;
    },
    onSuccess: async () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
      // Re-fetch the current user so the Zustand store gets fresh fieldOverrides
      try {
        const { data } = await authApi.getMe();
        setUser(data.user);
        queryClient.setQueryData(['auth', 'profile'], data.user);
      } catch {
        // If the current user doesn't have this role, that's fine
      }
    },
  });
}

export function useDeleteRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rateId }: { id: string; rateId: string }) => {
      await adminApi.deleteEmploymentRoleRate(id, rateId);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

// ─── Hospital Bonuses ─────────────────────────────────────────

export function useCreateHospitalBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, data }: { roleId: string; rateId: string; data: { hospitalId: string; bonusAmount: number } }) => {
      const { data: result } = await adminApi.createHospitalBonus(roleId, rateId, data);
      return result.bonus;
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateHospitalBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, bonusId, data }: { roleId: string; rateId: string; bonusId: string; data: { bonusAmount: number } }) => {
      const { data: result } = await adminApi.updateHospitalBonus(roleId, rateId, bonusId, data);
      return result.bonus;
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteHospitalBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, bonusId }: { roleId: string; rateId: string; bonusId: string }) => {
      await adminApi.deleteHospitalBonus(roleId, rateId, bonusId);
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

// ─── Supervision Bonuses ─────────────────────────────────────────

export function useCreateSupervisionBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, data }: { roleId: string; rateId: string; data: { hospitalId: string; bonusPerUnit: number; sourceEmploymentRoleIds: string[] } }) => {
      const { data: result } = await adminApi.createSupervisionBonus(roleId, rateId, data);
      return result.bonus;
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateSupervisionBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, bonusId, data }: { roleId: string; rateId: string; bonusId: string; data: { bonusPerUnit?: number; sourceEmploymentRoleIds?: string[] } }) => {
      const { data: result } = await adminApi.updateSupervisionBonus(roleId, rateId, bonusId, data);
      return result.bonus;
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteSupervisionBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, rateId, bonusId }: { roleId: string; rateId: string; bonusId: string }) => {
      await adminApi.deleteSupervisionBonus(roleId, rateId, bonusId);
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

// ─── Allowances ─────────────────────────────────────────

export function useEmploymentRoleAllowances(employmentRoleId: string | undefined) {
  return useQuery({
    queryKey: ['employment-roles', employmentRoleId, 'allowances'],
    queryFn: async () => {
      const { data } = await http.get(`/employment-roles/${employmentRoleId}/allowances`);
      return data.allowances;
    },
    enabled: !!employmentRoleId,
  });
}

export function useCreateEmploymentRoleAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employmentRoleId, data }: { employmentRoleId: string; data: { allowanceTypeId: string; amount: number; period: string; notes?: string | null } }) => {
      const res = await http.post(`/employment-roles/${employmentRoleId}/allowances`, data);
      return res.data.allowance;
    },
    onSuccess: (_, { employmentRoleId }) => {
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId, 'allowances'] });
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId] });
      qc.invalidateQueries({ queryKey: ['employment-roles'] });
    },
  });
}

export function useUpdateEmploymentRoleAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employmentRoleId, allowanceId, data }: { employmentRoleId: string; allowanceId: string; data: { amount?: number; period?: string; notes?: string | null } }) => {
      const res = await http.put(`/employment-roles/${employmentRoleId}/allowances/${allowanceId}`, data);
      return res.data.allowance;
    },
    onSuccess: (_, { employmentRoleId }) => {
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId, 'allowances'] });
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId] });
    },
  });
}

export function useDeleteEmploymentRoleAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employmentRoleId, allowanceId }: { employmentRoleId: string; allowanceId: string }) => {
      await http.delete(`/employment-roles/${employmentRoleId}/allowances/${allowanceId}`);
    },
    onSuccess: (_, { employmentRoleId }) => {
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId, 'allowances'] });
      qc.invalidateQueries({ queryKey: ['employment-roles', employmentRoleId] });
      qc.invalidateQueries({ queryKey: ['employment-roles'] });
    },
  });
}

// ─── Hospital Subset & Sync ─────────────────────────────────────────

export function useEmploymentRoleHospitalSubset(roleId: string | undefined) {
  return useQuery({
    queryKey: KEYS.hospitalSubset(roleId!),
    queryFn: async () => {
      const { data } = await adminApi.getHospitalSubset(roleId!);
      return data.hospitalSubset;
    },
    enabled: !!roleId,
  });
}

export function useUpdateHospitalSubset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, hospitalIds }: { roleId: string; hospitalIds: string[] }) => {
      const { data } = await adminApi.updateHospitalSubset(roleId, hospitalIds);
      return data.hospitalSubset;
    },
    onSuccess: (_, { roleId }) => {
      qc.invalidateQueries({ queryKey: KEYS.hospitalSubset(roleId) });
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useSyncHospitals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roleId: string) => {
      const { data } = await adminApi.syncHospitals(roleId);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}
